// src/pages/misc/landing/LandingMenu.tsx
// [HANDOFF] Slide-in side menu shared by the landing (LandingPage.tsx) and every public page (public/PublicShell.tsx).
//   Props: open / lang ('es' | 'en') and callbacks. The parent owns the state.
//   Entries, in order: "Empezar ahora" (gold button) -> /onboarding, then Cómo funciona, Socios / Programa de referidos, Preguntas frecuentes,
//   Contacto, Iniciar sesión (-> /login, NOT part of the swap), and the Español / English switch at the bottom.
// [URL-RENAME] Navigates with typed Spanish paths. When English URLs arrive change: /como-funciona -> /how-it-works, /socios -> /partners,
//   /preguntas -> /faq, /contacto -> /contact (that one already exists as an older page, decide which wins). /onboarding and /login stay.
// [I18N] The English / Español switch only changes the `lang` state the parent passes; the page texts themselves are still Spanish.
// WU-164 D5 (QA-F04): the menu's own contacto view, which nothing opened, is gone with its WhatsApp button and its note form; the
//   Contacto entry opens the /contacto page, which carries the WhatsApp link and the mail form (ContactPage.tsx; docs/manifest/WU-164.md
//   SESSION 1 RECORD D5).
// [SPACING] Panel / scrim / link sizes live in landing.css (.vl-panel, .vl-scrim, .vl-menu-link, .vl-menu-cta).
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Rocket } from 'lucide-react';
import { VENTAJE_LOGIN_URL } from '../../../config';
import { ENGLISH_AVAILABLE } from '../../../contexts/languages/LanguageContext';

interface LandingMenuProps {
  open: boolean;
  lang: 'es' | 'en';
  onClose: () => void;
  onLang: (lang: 'es' | 'en') => void;
}

const LandingMenu: React.FC<LandingMenuProps> = ({
  open,
  lang,
  onClose,
  onLang,
}) => {
  const navigate = useNavigate();

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <>
      <div
        className={`vl-scrim${open ? ' on' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside className={`vl-panel${open ? ' on' : ''}`} aria-hidden={!open}>
        <div className="vl-panel-head">
          <span className="vl-logo">Ventaje</span>
          <button type="button" className="vl-close-x" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div>
          <button
            type="button"
            className="btn-gold vl-menu-cta inline-flex items-center justify-center gap-2 min-h-[52px] rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em]"
            onClick={() => go('/onboarding')}
          >
            <Rocket size={16} /> Empezar ahora
          </button>
          <button type="button" className="vl-menu-link" onClick={() => go('/como-funciona')}>
            Cómo funciona
          </button>
          <button type="button" className="vl-menu-link" onClick={() => go('/socios')}>
            Socios / Programa de referidos
          </button>
          <button type="button" className="vl-menu-link" onClick={() => go('/preguntas')}>
            Preguntas frecuentes
          </button>
          <button type="button" className="vl-menu-link" onClick={() => go('/contacto')}>
            Contacto
          </button>
          <button type="button" className="vl-menu-link" onClick={() => window.location.assign(VENTAJE_LOGIN_URL)}>
            <span className="inline-flex items-center gap-2"><LogIn size={16} /> Iniciar sesión</span>
          </button>
          {ENGLISH_AVAILABLE && (
            <div className="vl-panel-foot">
              <span className="vl-tag">Idioma</span>
              <div className="vl-lang">
                <button
                  type="button"
                  className={lang === 'es' ? 'on' : ''}
                  onClick={() => onLang('es')}
                >
                  Español
                </button>
                <button
                  type="button"
                  className={lang === 'en' ? 'on' : ''}
                  onClick={() => onLang('en')}
                >
                  English
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default LandingMenu;
